/**
 * Carousel Shader System
 * 
 * This shader creates smooth, distorted transitions between images in the carousel.
 * It uses a displacement mapping technique where the red channel of images is used
 * to create distortion effects during transitions.
 * 
 * The effect works by:
 * 1. Taking two images (current and next)
 * 2. Using their red channel values as displacement maps
 * 3. Distorting UV coordinates based on these values
 * 4. Mixing the distorted results based on transition progress
 */

// Vertex shader - Simple pass-through that provides UV coordinates
export const vertex = `
  varying vec2 vUv; // UV coordinates passed to fragment shader
  
  void main() {
    vUv = uv; // Pass UV coordinates to fragment shader
    // Standard vertex transformation: object space -> world space -> camera space -> clip space
    gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
  }
`;

// Fragment shader - Creates the displacement transition effect
export const fragment = `
  varying vec2 vUv; // UV coordinates from vertex shader
  
  // Uniforms (values passed from JavaScript)
  uniform sampler2D currentImage; // The image currently being displayed
  uniform sampler2D nextImage;    // The image transitioning to
  uniform float dispFactor;       // Transition progress (0.0 = current, 1.0 = next)
  uniform float direction;        // Direction of transition (1.0 or -1.0 for left/right)

  void main() {
    vec2 uv = vUv;
    vec4 _currentImage;
    vec4 _nextImage;
    
    // Controls how strong the displacement effect is
    // Higher values = more dramatic distortion
    float intensity = 0.6;

    // Sample both images at the original UV coordinates
    // These will be used as displacement maps (using their red channel)
    vec4 orig1 = texture2D(currentImage, uv);
    vec4 orig2 = texture2D(nextImage, uv);

    // Create distorted UV coordinates for the current image
    // The distortion increases as dispFactor approaches 1.0 (end of transition)
    // Uses the red channel of the next image as the displacement map
    vec2 distortedPosition = vec2(
      uv.x + direction * (dispFactor * (orig2.r * intensity)),
      uv.y + direction * (dispFactor * (orig2 * intensity))
    );

    // Create distorted UV coordinates for the next image
    // The distortion decreases as dispFactor approaches 1.0 (end of transition)
    // Uses the red channel of the current image as the displacement map
    vec2 distortedPosition2 = vec2(
      uv.x - direction * ((1.0 - dispFactor) * (orig1.r * intensity)),
      uv.y - direction * ((1.0 - dispFactor) * (orig1 * intensity))
    );

    // Sample both images using their respective distorted coordinates
    _currentImage = texture2D(currentImage, distortedPosition);
    _nextImage = texture2D(nextImage, distortedPosition2);

    // Mix the two distorted images based on transition progress
    // dispFactor = 0.0: show only current image
    // dispFactor = 1.0: show only next image
    // dispFactor = 0.5: 50/50 mix of both images
    vec4 finalTexture = mix(_currentImage, _nextImage, dispFactor);
    gl_FragColor = finalTexture;
  }
`;
